import { useRef, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { toast } from "sonner";
import { Bold, Italic, Underline, Heading2, Heading3, List, ListOrdered, Quote, Link2, ImagePlus, Undo2, Redo2, Minus, Loader2 } from "lucide-react";
import { uploadImage, formatApiError } from "@/lib/api";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export const toEditorHtml = (text = "") => {
  if (/<\/?(p|h2|h3|ul|ol|img|strong|em|blockquote)\b/i.test(text)) return text;
  return text.split(/\n{2,}/).filter((b) => b.trim()).map((b) => {
    if (b.startsWith("## ")) {
      const [head, ...rest] = b.split("\n");
      return `<h2>${esc(head.slice(3))}</h2>${rest.length ? `<p>${esc(rest.join(" "))}</p>` : ""}`;
    }
    return `<p>${esc(b)}</p>`;
  }).join("");
};

const Tool = ({ on, active, label, testId, children, disabled }) => (
  <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={on} disabled={disabled} title={label} aria-label={label} data-testid={testId}
    className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg transition-colors disabled:opacity-30 ${active ? "bg-volt text-ink" : "text-zinc-200 hover:bg-white/10"}`}>
    {children}
  </button>
);

export const RichEditor = ({ value, onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3] }, link: { openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: null } } }),
      Image.configure({ HTMLAttributes: { loading: "lazy" } }),
    ],
    content: toEditorHtml(value),
    shouldRerenderOnTransaction: true,
    editorProps: { attributes: { class: "prose-bp", "data-testid": "admin-article-editor" } },
    onUpdate: ({ editor: ed }) => onChange(ed.getHTML()),
  });
  if (!editor) return null;
  const c = () => editor.chain().focus();
  const setLink = () => {
    const { from, to } = editor.state.selection;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("Masukkan link (kosongkan untuk menghapus link):", prev || "https://");
    if (url === null) return;
    const href = url.trim();
    const chain = editor.chain().focus().setTextSelection({ from, to });
    if (!href || href === "https://") return chain.extendMarkRange("link").unsetLink().run();
    if (from === to && !editor.isActive("link")) return chain.insertContent({ type: "text", text: href, marks: [{ type: "link", attrs: { href } }] }).run();
    if (from === to) return chain.extendMarkRange("link").setLink({ href }).run();
    chain.setLink({ href }).run();
  };
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const alt = window.prompt("Deskripsi singkat gambar (untuk SEO, contoh: cara convert pulsa di aplikasi BagiPulsa):", file.name.replace(/\.[^.]+$/, "").replace(/[-_]+/g, " ")) || "";
    setBusy(true);
    try {
      const url = await uploadImage(file);
      c().setImage({ src: url, alt }).run();
      toast.success("Gambar dimasukkan");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bp-editor overflow-hidden rounded-xl border border-white/10 bg-ink focus-within:border-volt" data-testid="admin-article-editor-wrap">
      <div className="no-scrollbar sticky top-0 z-10 flex gap-1 overflow-x-auto border-b border-white/10 bg-surface p-1.5" data-testid="admin-editor-toolbar">
        <Tool on={() => c().toggleBold().run()} active={editor.isActive("bold")} label="Tebal" testId="editor-bold"><Bold className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleItalic().run()} active={editor.isActive("italic")} label="Miring" testId="editor-italic"><Italic className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleUnderline().run()} active={editor.isActive("underline")} label="Garis bawah" testId="editor-underline"><Underline className="h-4 w-4" /></Tool>
        <span className="mx-1 w-px shrink-0 bg-white/10" />
        <Tool on={() => c().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })} label="Sub judul besar" testId="editor-h2"><Heading2 className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })} label="Sub judul kecil" testId="editor-h3"><Heading3 className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleBulletList().run()} active={editor.isActive("bulletList")} label="Daftar titik" testId="editor-bullet"><List className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleOrderedList().run()} active={editor.isActive("orderedList")} label="Daftar nomor" testId="editor-ordered"><ListOrdered className="h-4 w-4" /></Tool>
        <Tool on={() => c().toggleBlockquote().run()} active={editor.isActive("blockquote")} label="Kutipan" testId="editor-quote"><Quote className="h-4 w-4" /></Tool>
        <Tool on={() => c().setHorizontalRule().run()} label="Garis pemisah" testId="editor-hr"><Minus className="h-4 w-4" /></Tool>
        <span className="mx-1 w-px shrink-0 bg-white/10" />
        <Tool on={setLink} active={editor.isActive("link")} label="Link" testId="editor-link"><Link2 className="h-4 w-4" /></Tool>
        <Tool on={() => fileRef.current?.click()} disabled={busy} label="Upload gambar" testId="editor-image">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}</Tool>
        <span className="mx-1 w-px shrink-0 bg-white/10" />
        <Tool on={() => c().undo().run()} disabled={!editor.can().undo()} label="Urungkan" testId="editor-undo"><Undo2 className="h-4 w-4" /></Tool>
        <Tool on={() => c().redo().run()} disabled={!editor.can().redo()} label="Ulangi" testId="editor-redo"><Redo2 className="h-4 w-4" /></Tool>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={onFile} data-testid="editor-image-input" />
      <EditorContent editor={editor} />
    </div>
  );
};
